import { FC, PropsWithChildren } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';

import { IUnitsPriceAvailabilityInformation } from '../type';
import { useConfig } from '../../../context/ConfigProvider';

export const getProperValue = (value: number, roundOffUnitDisplayPrice?: boolean): string => {
  if(value % 1 === 0){
    return roundOffUnitDisplayPrice ? getRoundOffValue(value)?.toString() : value.toString()
  }
  return roundOffUnitDisplayPrice ? getRoundOffValue(value)?.toFixed(2) : value.toFixed(2);
}
  

export const getRoundOffValue = (value: number): number => {
  if (value === null || value === undefined || isNaN(value)) {
    return value;
  }
  return Math.round(value);
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    padding: 0,
    margin: 0,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.paper,
  },
}));

const ExtraInfo: FC<IUnitsPriceAvailabilityInformation> = (props) => {
  const { configuration } = useConfig();
  return (
    <CustomTooltip
      arrow
      // open
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -14],
              },
            },
          ],
        },
      }}
      title={
        <Card sx={{ p: 1 }} elevation={5}>
          <Typography variant='caption'>
            The following fees shall apply:
            <Box sx={{ pl: 2 }}>
              <ul style={{ padding: 0, margin: 0 }}>
                {(props.fees || []).map((fee, index) => (
                  <li key={index}>{`${fee.description} ${fee.description.endsWith('Fee') ? '' : 'Fee'
                    }: $${getProperValue(fee.fee, configuration?.roundOffUnitDisplayPrice)}${fee.frequency === 'monthly'
                      ? '/mo'
                      : ' (' + fee.frequency + ')'
                    }`}</li>
                ))}
              </ul>
            </Box>
          </Typography>
        </Card>
      }
    >
      <InfoIcon sx={(theme) => ({ color: theme.palette.grey[500] })} />
    </CustomTooltip>
  );
};

export const Pricing: FC<
  PropsWithChildren<IUnitsPriceAvailabilityInformation>
> = (props) => {
  const { configuration } = useConfig();
  switch (true) {
    // Show discounted price if it is defined and less than startPrice
    case props.discountedPriceStart !== null &&
      props.discountedPriceStart !== undefined &&
      props.discountedPriceStart < props.startPrice:
      return (
        <Box>
          <Box display='flex' flexDirection='row' alignItems='center'>
            <Typography
              variant='caption'
              color='GrayText'
              sx={{ textDecoration: 'line-through', mr: 0.5 }}
            >
              {`${props.currencySymbol}${getProperValue(
                props.startPrice, configuration?.roundOffUnitDisplayPrice
              )}/${props.pricePer}`}
            </Typography>
            <Typography
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
              }}
              variant='subtitle1'
            >
              <Typography
                variant='h6'
              // sx={{ fontWeight: "bold" }}
              >{`${props.currencySymbol}${getProperValue(
                props.discountedPriceStart, configuration?.roundOffUnitDisplayPrice
              )}`}</Typography>
              {`/${props.pricePer}`}
            </Typography>

            {props.children}
          </Box>
          {configuration.showFeeDetails && (
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              sx={{ justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}
            >
              <Typography variant='caption'>
                {`+ $${getProperValue(props.totalApplicableFees, configuration?.roundOffUnitDisplayPrice)} in fees`}
              </Typography>
              <ExtraInfo {...props} />
            </Box>
          )}
        </Box>
      );
    // its a range .. so we show Starts from
    case Boolean(props.startPrice) && Boolean(props.endPrice):
      return (
        <Box>
          <Box display='flex' flexDirection='row' alignItems='center'>
            <Typography variant='caption' color='GrayText' sx={{ mr: 0.5 }}>
              Starts at
            </Typography>
            <Typography
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
              }}
              variant='subtitle1'
            >
              <Typography
                variant='h6'
              // sx={{ fontWeight: "bold" }}
              >{`${props.currencySymbol}${getProperValue(
               props.startPrice, configuration?.roundOffUnitDisplayPrice
              )}`}</Typography>
              {`/${props.pricePer}`}
            </Typography>
            {props.children}
          </Box>
          {configuration.showFeeDetails && (
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              sx={{ justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}
            >
              <Typography variant='caption'>
                {`+ $${getProperValue(props.totalApplicableFees, configuration?.roundOffUnitDisplayPrice)} in fees`}
              </Typography>
              <ExtraInfo {...props} />
            </Box>
          )}
        </Box>
      );
    default:
      return (
        <Box>
          <Box display='flex' flexDirection='row' alignItems='center'>
            {props?.strikethroughPrice ? <Typography
              variant='caption'
              color='GrayText'
              sx={{ textDecoration: 'line-through', mr: 0.5 }}
            >
              {`${props.currencySymbol}${getProperValue(props.strikethroughPrice, configuration?.roundOffUnitDisplayPrice)}/${props.pricePer
                }`}
            </Typography> : ""}
            <Typography
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
              }}
              variant='subtitle1'
            >
              <Typography
                variant='h6'
              // sx={{ fontWeight: "bold" }}
              >{`${props.currencySymbol}${getProperValue(
                props.startPrice, configuration?.roundOffUnitDisplayPrice
              )}`}</Typography>
              {`/${props.pricePer}`}
            </Typography>
            {props.children}
          </Box>
          {configuration.showFeeDetails && (
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              sx={{ justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}
            >
              <Typography variant='caption'>
                {`+ $${getProperValue(props.totalApplicableFees, configuration?.roundOffUnitDisplayPrice)} in fees`}
              </Typography>
              <ExtraInfo {...props} />
            </Box>
          )}
        </Box>
      );
  }
};
